import { createRoute, redirect } from '@tanstack/react-router'
import { areaRoute } from '@/app/routes/area-layout'
import { lazyPage } from '@/lib/lazy-page'
import { booleanParam } from '@/lib/search-params'
import { assetsGridContract, firewallRulesGridContract, normalizeDataGridUrlState, unrecognizedAssetsGridContract } from '@/modules/data-grid'
import { DESCENDANT_ONLY_COLUMNS } from './features/asset-list/assets-grid'
import { decodePrinterMetersSearch } from './features/asset-detail/printer-meter-history'

export const EntityAssetDetail = lazyPage(() => import('./features/asset-detail/entity-asset-detail.lazy'), 'EntityAssetDetail')

export const entityAssetDetailRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'assets/$assetId',
  component: EntityAssetDetail,
})

export const EntityAssetManagement = lazyPage(() => import('./features/asset-detail/entity-asset-management.lazy'), 'EntityAssetManagement')

export const entityAssetManagementRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'assets/$assetId/management',
  component: EntityAssetManagement,
})

export const EntityAssetNetwork = lazyPage(() => import('./features/asset-detail/entity-asset-network.lazy'), 'EntityAssetNetwork')

export const entityAssetNetworkRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'assets/$assetId/network',
  component: EntityAssetNetwork,
})

export const EntityAssetSecurity = lazyPage(() => import('./features/asset-detail/entity-asset-security.lazy'), 'EntityAssetSecurity')

export const entityAssetSecurityRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'assets/$assetId/security',
  component: EntityAssetSecurity,
})

export const EntityAssetFirewallRules = lazyPage(() => import('./features/asset-detail/entity-asset-firewall-rules.lazy'), 'EntityAssetFirewallRules')

export type FirewallRulesSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
  columns?: string
}

export function decodeFirewallRulesSearch(search: Record<string, unknown>): FirewallRulesSearch {
  return normalizeDataGridUrlState(search, firewallRulesGridContract)
}

export const entityAssetFirewallRulesRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'assets/$assetId/security/firewall-rules',
  component: EntityAssetFirewallRules,
  validateSearch: decodeFirewallRulesSearch,
})

export const EntityAssetSettings = lazyPage(() => import('./features/asset-detail/entity-asset-settings.lazy'), 'EntityAssetSettings')

export const entityAssetSettingsRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'assets/$assetId/settings',
  component: EntityAssetSettings,
})

export const EntityAssetSource = lazyPage(() => import('./features/asset-detail/entity-asset-source.lazy'), 'EntityAssetSource')

export const entityAssetSourceRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'assets/$assetId/source',
  component: EntityAssetSource,
})

export const EntityAssetMeters = lazyPage(() => import('./features/asset-detail/entity-asset-meters.lazy'), 'EntityAssetMeters')

export const entityAssetMetersRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'assets/$assetId/meters',
  component: EntityAssetMeters,
  validateSearch: decodePrinterMetersSearch,
})

export const EntityAssetSupplies = lazyPage(() => import('./features/asset-detail/entity-asset-supplies.lazy'), 'EntityAssetSupplies')

export const entityAssetSuppliesRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'assets/$assetId/supplies',
  component: EntityAssetSupplies,
})

export type EntityAssetsSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
  columns?: string
  includeDescendants?: boolean
}

const descendantOnlyColumns: readonly string[] = DESCENDANT_ONLY_COLUMNS
const columnsWithoutChildren = Object.fromEntries(Object.entries(assetsGridContract.columns)
  .filter(([column]) => !descendantOnlyColumns.includes(column)))

export function decodeEntityAssetsSearch(
  search: Record<string, unknown>,
): EntityAssetsSearch {
  const criteriaColumns = booleanParam(search.includeDescendants) === false
    ? columnsWithoutChildren
    : assetsGridContract.columns
  return {
    ...normalizeDataGridUrlState(search, { columns: criteriaColumns, parameters: assetsGridContract.parameters }),
    columns: normalizeDataGridUrlState(search, assetsGridContract).columns,
  }
}

export const EntityAssets = lazyPage(() => import('./features/asset-list/entity-assets.lazy'), 'EntityAssets')

export const entityAssetsRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'assets',
  component: EntityAssets,
  validateSearch: decodeEntityAssetsSearch,
})

export const EntityPrintSettings = lazyPage(
  () => import('./features/print-settings/entity-print-settings.lazy'),
  'EntityPrintSettings',
)

export const entityPrintSettingsRoute = createRoute({
  getParentRoute: () => areaRoute('settings'),
  path: 'print',
  component: EntityPrintSettings,
  staticData: { title: 'Print settings' },
})

export const EntityPrinterPolicies = lazyPage(
  () => import('./features/printer-policies/entity-printer-policies.lazy'),
  'EntityPrinterPolicies',
)

export const entityPrinterPoliciesRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'assets/configuration/printer-policies',
  component: EntityPrinterPolicies,
  validateSearch: decodePrinterPoliciesSearch,
  staticData: { title: 'Printer policies' },
})

export const entityPrinterPolicyRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'assets/configuration/printer-policies/$policyId',
  beforeLoad: ({ params: { entityId, policyId } }) => {
    throw redirect({
      to: '/$entityId/assets/configuration/printer-policies',
      params: { entityId },
      search: { policy: policyId },
      replace: true,
    })
  },
  staticData: { title: 'Printer policy' },
})

export function decodePrinterPoliciesSearch(search: Record<string, unknown>): { policy?: string } {
  return { policy: typeof search.policy === 'string' ? search.policy.toLowerCase()
    : search.policy === undefined ? undefined : '' }
}

export type EntityUnrecognizedAssetsSearch = {
  q?: string
  qColumns?: string
  filters?: string
  sort?: string
  columns?: string
}

export function decodeEntityUnrecognizedAssetsSearch(
  search: Record<string, unknown>,
): EntityUnrecognizedAssetsSearch {
  return normalizeDataGridUrlState(search, unrecognizedAssetsGridContract)
}

export const EntityUnrecognizedAssets = lazyPage(() => import('./features/unrecognized-assets/entity-unrecognized-assets.lazy'), 'EntityUnrecognizedAssets')

export const entityUnrecognizedAssetsRoute = createRoute({
  getParentRoute: () => areaRoute('dataCollection'),
  path: 'assets/unrecognized',
  component: EntityUnrecognizedAssets,
  validateSearch: decodeEntityUnrecognizedAssetsSearch,
})
